<template>
    <div class="o-resource-list">
        <span class="app-label">{{ $t('app.ShowTranslationsForLanguages')}} </span>
        <div class="selected-language">
            <Select
                :value="selectedLanguageCodes"
                filterable
                multiple
                @on-change="selectedLanguageChangeHandler"
                @on-open-change="openChangeHandler"
            >
                <Option
                    v-for="language in languages"
                    :key="language.id"
                    :value="language.code"
                    >{{ language.name }} ({{ language.code }})</Option
                >
            </Select>
        </div>

        <div class="btns"> 
            <Button type="primary" @click="addResource">
                <i class="uil uil-plus" />
                {{ $t('app.AddResource') }}
            </Button>
        </div>

        <a-table
            class="app-table"
            pagination
            v-bind="{
                data: resources,
                page: pageNum,
                pages: totalPages,
                pageSize,
                isLoading: isLoading
            }"
            @onLoadData="getResources"
        >
            <template slot="header">
                <div class="app-col">
                    <div class="holder">
                         {{ $t('app.Key') }}
                    </div>
                </div>
                <div class="app-col resource-kind">
                    {{ $t('app.ResourceKind') }}
                    <Select v-model="resourceKindCode">
                        <Option 
                            v-for="resourceKind in resourceKindList" 
                            :key="resourceKind.code" 
                            :value="resourceKind.code">
                            {{ resourceKind.name }}
                        </Option>
                    </Select>
                </div>
                <div class="app-col primary-language">
                    {{ $t('app.PrimaryLanguage') }}
                    <Select v-model="languageCode">
                        <Option 
                            v-for="language in languageList" 
                            :key="language.code" 
                            :value="language.code">
                            {{ language.name }}
                        </Option>
                    </Select>
                </div>

                <div class="tags app-col">
                    {{ $t('app.Tags') }}
                    <Select v-model="selectedTags" >
                        <Option 
                            v-for="tag in tags" 
                            :key="tag" 
                            :value="tag">
                            {{ tag }}
                        </Option>
                    </Select>
                </div>

                <div class="value app-col">
                    {{ $t('app.Value') }}
                </div>

                <div
                    v-for="langCode of selectedLanguageCodes"
                    :key="langCode"
                    class="lang app-col"
                >
                    {{ langMap[langCode] }}
                </div>

                <div class="actions app-col">
                    {{ $t('app.Actions') }}
                </div>
            </template>

            <template slot="row" slot-scope="{ row }">
                <div class="app-col">
                    <div class="holder">
                        <span v-if="row.kind == 'googledoc'">
                            <o-google-doc-link
                                v-if="docMap[row.key] && docMap[row.key][row.primaryLanguage]"
                                :url="docMap[row.key][row.primaryLanguage].url"
                                :extrenalId="row.key"
                                :title="row.name"
                                :onSetGoogleDocPermissions="onSetGoogleDocPermissions"
                            />
                        </span>
                        <span v-else>
                            {{ row.key }}
                        </span>
                    </div>
                </div>

                <div class="app-col">
                    <span> {{ row.kind }} </span>
                </div>

                <div class="app-col">
                    <span> {{ langMap[row.primaryLanguage] }} </span>
                </div>

                <div class="tags app-col">
                    <span> {{ row.tags.join(', ') }} </span>
                </div>

                <div class="app-col">
                    <span class="long-text"> {{ row.value && row.value.primary ? row.value.primary : row.value }} </span>
                </div>

                <div
                    v-for="langCode of selectedLanguageCodes"
                    :key="langCode"
                    class="lang app-col"
                >
                    <div>
                        {{ prepareLocalizationValue(row, langCode) }}
                    </div>

                    <o-google-doc-link
                        v-if="getDoc(row, langCode)"
                        :url="getDoc(row, langCode).url"
                        :extrenalId="getDoc(row, langCode).externalId"
                        :title="$t('app.Open')"
                        :onSetGoogleDocPermissions="onSetGoogleDocPermissions"
                    />
                </div>

                <div class="actions app-col">
                    <i
                        class="uil uil-trash-alt delete"
                        @click="deleteResource(row)"
                        :title="$t('app.DeleteResource')"
                    />
                    <i
                        class="uil uil-edit edit"
                        @click="editResource(row)"
                        :title="$t('app.EditResource')"
                    />
                    <i v-if="row.kind == 'googledoc'"
                        class="uil uil-redo edit"
                        @click="updateGoogleDoc(row)"
                        :title="$t('app.UpdateGoogleDoc')"
                    />
                    <i
                        v-if="isAutoTranslation(row)"
                        class="uil uil-google google"
                        @click="translationGoogle(row)"
                        :title="$t('app.TranslationGoogle')"
                    />
                </div>
            </template>

            <template slot="empty">
                <div class="empty">
                    <div class="empty-holder">
                        <p>{{ $t('app.No_resources') }}</p>
                    </div>
                </div>
            </template>
        </a-table>
    </div>
</template>

<script>
import {
    Button,
} from 'view-design'
import ATable from '@atoms/a-table'
import _ from 'lodash'
import { ResourceKind } from '@/constants/resource-kind.js'
import { Option, Select } from 'view-design'
import OGoogleDocLink from '@organisms2/o-google-doc-link'
import { Role } from '@/constants/role.js'

export default {
    name: 'OResourceList',
    components: {
        Button,
        ATable,
        Option, 
        Select,
        OGoogleDocLink
    },
    props: {
        resources: {
            type: Array,
            required: true,
        },
        languages: {
            type: Array,
            required: true,
        },
        projectInfo: {

        },
        onGetResources: {
            type: Function,
            required: true,
        },
        onSearchGoogleDocs: {
            type: Function,
            required: true,
        },
        onSetGoogleDocPermissions: {
            type: Function,
            required: true,
        },
        tags: Array
    },
    data() {
        return {
            docMap: {},
            isLoading: false,
            pageNum: 1,
            totalPages: 1,
            pageSize: 20,
            resourceKindCode: "all",
            languageCode: "all",
            languageList: [],
            langMap: {},
            selectedTags: null,
            selectedLanguageCodes: [],
            isPopupOpen: false,
            resourceKindList: [
                {
                    code: "all",
                    name: this.$t('app.All')
                },
                {
                    code: ResourceKind.string,
                    name: ResourceKind.string
                },
                {
                    code: ResourceKind.richtext,
                    name: ResourceKind.richtext
                },
                {
                    code: ResourceKind.googledoc,
                    name: ResourceKind.googledoc
                },
            ]
        }
    },
    computed: {
    },
    watch: {
        resourceKindCode() {
            this.reloadResources()
        },
        languageCode() {
            this.reloadResources()
        },
        selectedTags() {
            this.reloadResources()
        }
    },
    async created() {
        this.languages.forEach(lang => {
            this.langMap[lang.code] = `${lang.name} (${lang.code})`
        })
        
        this.languageList = [
            {
                code: "all",
                name: this.$t('app.All')
            },
            ...this.languages.map(lang => {return { code:lang.code, name: `${lang.name} (${lang.code})` }})
        ]
        await this.reloadResources();
    },
    methods: {
        async getResources({ page }) {
            if (this.isPopupOpen) return;

            this.isLoading = true;
            try {
                const params = {
                    pageNum: page,
                    pageSize: this.pageSize,
                    cutValue: 300
                }

                if (this.resourceKindCode !== "all") {
                    params.kind = this.resourceKindCode
                }
                
                if (this.languageCode !== "all") {
                    params.primaryLang = this.languageCode
                }

                if (this.selectedLanguageCodes && this.selectedLanguageCodes.length > 0) {
                    params.langs="primary," + this.selectedLanguageCodes.join(",");
                }

                if(this.selectedTags ){
                    params.tags = this.selectedTags
                }

                const { items, meta } = await this.onGetResources(params);
                
                let resourceKeys = [];
                let langCodes = [];
                this.docMap = {};
                this.resources.splice(0);
                for (let i =0 ; i < items.length; i++) {
                    const item = items[i];
                    this.$set(this.resources, i, items[i]);
                    if (item.kind == 'googledoc') {
                        resourceKeys.push(item.key);
                        langCodes.push(item.primaryLanguage);
                        if (this.selectedLanguageCodes && this.selectedLanguageCodes.length > 0) {
                            langCodes.push(...this.selectedLanguageCodes);
                        }
                    }
                }
                
                resourceKeys = _.uniq(resourceKeys);
                langCodes = _.uniq(langCodes);
                if (resourceKeys.length > 0) {
                    const docRes = await this.onSearchGoogleDocs({
                        resourceKeys,
                        langCodes
                    })
                    docRes.items.forEach(doc => {
                        const docKey = doc.copiedFrom ? doc.copiedFrom : doc.externalId;
                        if (!this.docMap[docKey]) {
                            this.$set(this.docMap, docKey, {});
                        };
                        this.$set(this.docMap[docKey], doc.langCode, doc);
                    })
                }

                for (let i =0 ; i < this.resources.length; i++) {
                    const resource = this.resources[i];
                    if (this.docMap[resource.key] && this.docMap[resource.key][resource.primaryLanguage]) {
                        resource.url = this.docMap[resource.key][resource.primaryLanguage].url;
                    }
                }

                this.pageNum = meta.pageNum
                this.totalPages = meta.totalPages
                this.pageSize = meta.pageSize

                this.isLoading = false;

            } catch(err) {
                this.isLoading = false;
                throw err;
            }
        },
        showLoading() {
            this.isLoading = true;
        },
        addResource() {
            this.$emit("addResource");
        },
        deleteResource(resource) {
            this.$emit("deleteResource", resource);
        },
        editResource(resource) {
            this.$emit("editResource", resource);
        },
        updateGoogleDoc(resource) {
            this.$emit("updateGoogleDoc", resource);
        },
        translationGoogle(resource) {
            this.$emit('translationGoogle', resource)
        },
        isAutoTranslation(resource) {
            if (resource && resource.kind && resource.kind !== 'googledoc') {
                const isRole = this.projectInfo.roles.filter(i =>
                    [Role.owner, Role.admin].includes(i)
                )
                if (isRole && isRole.length) {
                    return true
                }
            }
            return false
        },
        async reloadResources() {
            await this.getResources({ page: 1 })            
        },
        async reloadCurrentPage() {
            await this.getResources({ page: this.pageNum })            
        },
        async selectedLanguageChangeHandler(codes) {
            this.selectedLanguageCodes = codes;
            await this.reloadResources()
        },
        async openChangeHandler(isOpen) {
            this.isPopupOpen = isOpen;
            await this.reloadResources()
        },
        getLanguageCodeFilter() {
            return this.languageCode;
        },
        getDoc(row, langCode) {
            if (row.kind == 'googledoc') {
                if (this.docMap[row.key] && this.docMap[row.key][langCode]) {
                    return this.docMap[row.key][langCode];
                }
            }
            return null;
        },
        prepareLocalizationValue(row, langCode) {
            if (row.value[langCode]) {
                return row.value[langCode];
            }
            return "";
        }
    },
}
</script>

<style lang="scss" scoped>
@import 'theme';
</style>
